import {
  AlphaQuoteEvaluationStatus,
  AlphaQuoteLineStatus,
  AlphaQuoteSyncStatus,
  type CalculationError,
  type LineItem,
  type LineItemDisplayStatus,
  type QuantityBreak,
  type ValueBase,
} from '@snap-types/models/auto-configurator/state';

/**
 * Based on a line item's status and evaluation results, determines the
 * appropriate display status for the line item, which is used to drive UI
 * indicators and messaging.
 */
export function deriveLineItemDisplayStatus(
  lineItem: LineItem,
  quantityBreaks: QuantityBreak[],
  isAwaitingLatestCalculation: boolean,
  calculationError: CalculationError | null = null,
): LineItemDisplayStatus {
  if (lineItem.status === AlphaQuoteLineStatus.PENDING_DELETE) {
    return 'pending-delete';
  }

  if (isAwaitingLatestCalculation) {
    return 'calculating';
  }

  if (
    lineItem.lastApplyFailedAt &&
    (!lineItem.lastAppliedAt ||
      lineItem.lastApplyFailedAt.getTime() > lineItem.lastAppliedAt.getTime())
  ) {
    return 'apply-error';
  }

  if (calculationError) {
    return 'calculation-error';
  }

  if (
    lineItem.hasSemsEmbed.value &&
    !(lineItem.sems1Style.value || lineItem.sems2Style.value)
  ) {
    return 'customer-requires-sems';
  }

  const evaluationStatuses = [
    lineItem.evaluationStatus,
    ...quantityBreaks.map((quantityBreak) => quantityBreak.evaluationStatus),
  ];

  if (
    evaluationStatuses.includes(AlphaQuoteEvaluationStatus.CALCULATION_ERROR)
  ) {
    return 'calculation-error';
  }

  if (
    evaluationStatuses.includes(AlphaQuoteEvaluationStatus.CONSULT_ENGINEERING)
  ) {
    return 'consult-engineering';
  }

  if (
    evaluationStatuses.includes(AlphaQuoteEvaluationStatus.INCOMPLETE) ||
    lineItem.calculatedData === null ||
    quantityBreaks.some(
      (quantityBreak) => quantityBreak.calculatedData === null,
    ) ||
    hasInvalidValues(lineItem, quantityBreaks)
  ) {
    return 'incomplete';
  }

  if (lineItem.syncStatus === AlphaQuoteSyncStatus.SYNCED) {
    return 'applied';
  }

  if (lineItem.syncStatus === AlphaQuoteSyncStatus.STALE) {
    return 'calculated-since-applied';
  }

  return 'calculated';
}

function hasInvalidValues(
  lineItem: LineItem,
  quantityBreaks: QuantityBreak[],
): boolean {
  return (
    getValues(lineItem).some((value) => !value.isValid) ||
    quantityBreaks.some((quantityBreak) =>
      getValues(quantityBreak).some((value) => !value.isValid),
    )
  );
}

function getValues(source: Record<string, unknown>): ValueBase[] {
  return Object.values(source).filter(isValueBase);
}

function isValueBase(value: unknown): value is ValueBase {
  return (
    typeof value === 'object' &&
    value !== null &&
    'isValid' in value &&
    'errors' in value &&
    'rawValue' in value
  );
}
